import { useState, useCallback, useEffect } from "react"
import { useParams } from "react-router"
import {
  useCreateEvaluationCaseApi,
  useBulkDeleteEvaluationCasesApi,
  useListEvaluationCasesApi,
  makeBulkDeleteEvaluationCasesRequest,
  makeCreateEvaluationCaseRequest,
} from "@/hooks/api/eval"
import { MergedEvaluationCase } from "../types"
import useToast from "@/hooks/useToast"
import { useTranslation } from "react-i18next"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"

interface UseCaseManagementReturn {
  // Add case modal state
  showAddCaseModal: boolean
  setShowAddCaseModal: (show: boolean) => void
  addCaseForm: { input: string; expected_output: string; note: string }
  addCaseError: string | null
  addCaseLoading: boolean
  handleAddCaseChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  handleAddCaseSubmit: (e: React.FormEvent) => Promise<void>

  // Delete state
  showDeleteConfirm: boolean
  setShowDeleteConfirm: (show: boolean) => void
  deleteLoading: boolean
  setDeleteLoading: (loading: boolean) => void
  handleDeleteSelected: () => void
  handleConfirmDelete: () => Promise<void>
  handleCancelDelete: () => void

  // Selection state
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  allSelected: boolean
  isIndeterminate: boolean
  handleSelectAll: (checked: boolean) => void
  handleSelectRow: (id: string, checked: boolean) => void

  // Copy functionality
  handleCopySelected: () => Promise<void>
}

export const useCaseManagement = (
  mergedCases: MergedEvaluationCase[],
  workspaceId: string | undefined,
  fetchCases: ReturnType<typeof useListEvaluationCasesApi>["fetchFn"],
  getCaseSearchParams?: () => URLSearchParams
): UseCaseManagementReturn => {
  const { agentId } = useParams()
  const { alertToast, successToast } = useToast()
  const { t } = useTranslation()

  // Add case modal state
  const [showAddCaseModal, setShowAddCaseModal] = useState(false)
  const [addCaseForm, setAddCaseForm] = useState({
    input: "",
    expected_output: "",
    note: "",
  })
  const [addCaseError, setAddCaseError] = useState<string | null>(null)
  const [addCaseLoading, setAddCaseLoading] = useState(false)

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    const visibleIds = new Set(mergedCases.map((caseData) => caseData.id))
    setSelectedIds((current) =>
      current.filter((caseId) => visibleIds.has(caseId))
    )
  }, [mergedCases])

  // Create case API
  const { fetchFn: createCase } = useCreateEvaluationCaseApi()

  // Delete functionality
  const { error: bulkDeleteError, fetchFn: bulkDeleteCases } =
    useBulkDeleteEvaluationCasesApi()

  const handleAddCaseChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAddCaseForm((f) => ({ ...f, [e.target.name]: e.target.value }))
    },
    []
  )

  const handleAddCaseSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setAddCaseError(null)
      if (!addCaseForm.input.trim()) {
        setAddCaseError(t("evaluation.evaluationTable.message.inputRequired"))
        return
      }
      if (!agentId || !workspaceId) {
        setAddCaseError(
          t("evaluation.evaluationTable.message.missingAgentOrWorkspace")
        )
        return
      }
      setAddCaseLoading(true)
      try {
        const requestPayload = makeCreateEvaluationCaseRequest(
          agentId,
          addCaseForm.input,
          addCaseForm.expected_output || undefined,
          undefined,
          undefined,
          undefined,
          addCaseForm.note || undefined
        )
        await createCase({
          jsonBody: requestPayload,
          searchParams: new URLSearchParams({ wid: workspaceId || "" }),
        })
        setShowAddCaseModal(false)
        setAddCaseForm({ input: "", expected_output: "", note: "" })
        // Refresh cases
        fetchCases({
          searchParams:
            getCaseSearchParams?.() ||
            new URLSearchParams({ wid: workspaceId || "" }),
        })
      } catch (err) {
        setAddCaseError(
          getUserFacingApiErrorMessage(
            err,
            t,
            t("evaluation.evaluationTable.message.createCaseError")
          )
        )
      } finally {
        setAddCaseLoading(false)
      }
    },
    [
      addCaseForm,
      agentId,
      workspaceId,
      createCase,
      fetchCases,
      getCaseSearchParams,
      t,
    ]
  )

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return
    setShowDeleteConfirm(true)
  }, [selectedIds.length])

  const handleConfirmDelete = useCallback(async () => {
    if (selectedIds.length === 0) return

    setDeleteLoading(true)

    const requestPayload = makeBulkDeleteEvaluationCasesRequest(selectedIds)
    try {
      await bulkDeleteCases({
        jsonBody: requestPayload,
        searchParams: new URLSearchParams({ wid: workspaceId || "" }),
      })
    } catch (error) {
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.message.deleteError"),
          fallbackDescription: "",
        }),
      })
      setDeleteLoading(false)
      return
    }

    // Only show success if no error occurred (error is handled by useEffect)
    if (!bulkDeleteError) {
      successToast({
        description: t("evaluation.evaluationTable.message.deleteSuccess", {
          count: selectedIds.length,
        }),
      })

      // Clear selection and refresh cases
      setSelectedIds([])
      fetchCases({
        searchParams:
          getCaseSearchParams?.() ||
          new URLSearchParams({ wid: workspaceId || "" }),
      })
    }

    setDeleteLoading(false)
    setShowDeleteConfirm(false)
  }, [
    selectedIds,
    workspaceId,
    bulkDeleteCases,
    bulkDeleteError,
    alertToast,
    successToast,
    t,
    fetchCases,
    getCaseSearchParams,
  ])

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  // Selection logic
  const allSelected =
    mergedCases.length > 0 && selectedIds.length === mergedCases.length
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < mergedCases.length

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? mergedCases.map((c) => c.id) : [])
    },
    [mergedCases]
  )

  const handleSelectRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id)
    )
  }, [])

  const handleCopySelected = useCallback(async () => {
    const selectedCases = mergedCases.filter((c) => selectedIds.includes(c.id))
    if (selectedCases.length === 0) return
    const text = selectedCases
      .map(
        (c) =>
          `===\n${t("evaluation.evaluationTable.table.score")}: ${typeof c.score === "number" ? c.score : t("evaluation.evaluationTable.table.noScore")}\n${t("evaluation.evaluationTable.table.reason")}: ${c.reason || ""}`
      )
      .join("\n")
    try {
      await navigator.clipboard.writeText(text)
      successToast({
        description: t("evaluation.evaluationTable.message.reasonsCopied", {
          count: selectedCases.length,
        }),
      })
    } catch (err) {
      // fallback: do nothing
    }
  }, [mergedCases, selectedIds, successToast, t])

  return {
    showAddCaseModal,
    setShowAddCaseModal,
    addCaseForm,
    addCaseError,
    addCaseLoading,
    handleAddCaseChange,
    handleAddCaseSubmit,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteLoading,
    setDeleteLoading,
    handleDeleteSelected,
    handleConfirmDelete,
    handleCancelDelete,
    selectedIds,
    setSelectedIds,
    allSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
    handleCopySelected,
  }
}
